/* eslint-disable camelcase */

// domainLookup
// extractKeys
// forceQueryDoubleQuotes
// formatLanguages
// formatLanguagesWithPercentages
// isFilterActive
// isWebSearchSaved
// keysStringFromStringifiedObject
// mapDomains
// mapFilterArray
// pageTitleLookup
// rehydrateDoubleQuotes
// removeFilter
// shouldWeShowSaveWebFilterPrompt
// stringifyWebSearchFilters
// swapSingleQuotesForDouble
// toggleFilter

import _ from "underscore";
import iso6931 from "iso-639-1";

import { allQuotesSingle, allQuotesDouble, allQuotesDoubleTemporary } from "lib/helpers/mediaHelpers.js";

export const domainLookup = ({ domain, domainDataLookup }) => {
	const { domain_hash } = domain;
	const data = domainDataLookup[domain_hash];
	console.log(data);
	return domain ? domain.domain : "";
};


// returns the exclusive/inclusive keys as a comma separated string
export const extractKeysFromFilters = ({ filters, exclusive }) => {
	const keysArray = [];
	filters.forEach(x => {		
		if (!!exclusive && x.active && x.activeNegation) {
			// Exclusive filter found
			keysArray.push(x.id);
		}
		else if (!exclusive && x.active && !x.activeNegation) {
			// Inclusive filter found
			keysArray.push(x.id);
		}
	});
	return keysArray.join(",");
};

// Split queryString on AND/OR and replace any single quotes wrapping the segements in double quotes
export const forceQueryDoubleQuotes = (queryString) => {
	if (queryString) {
		const segmentArray = queryString.split(/(AND|OR)/g);
		const formattedArray = [];
		let updatedString = "";

		_.each(segmentArray, (segment) => {
			const trimmedString = segment.trim();
			const quotedString = swapSingleQuotesForDouble(trimmedString);
			formattedArray.push(quotedString);
		});

		_.each(formattedArray, (segment, index) => {
			updatedString = `${updatedString} ${segment}`;
		});

		return updatedString.trim();
	} else {
		return "";
	}
};

// returns a string
export const formatLanguages = (langArray) => {
	let languages = "";
	const formattedArray = [];

	if (langArray) {
		_.each(langArray, (lang, index) => {
			const fullLanguage = languageLookup(lang);
			if (fullLanguage) {
				formattedArray.push(fullLanguage);
			}
		});

		if (formattedArray.length > 0) {
			_.each(formattedArray, (full, index) => {
				languages = `${languages}${full}${index < formattedArray.length - 1 ? ", " : ""}`;
			});
		} else {
			languages = langArray[0];
		}
	}

	return languages;
};

export const formatLanguagesWithPercentages = (langArray) => {
	let languages = "";

	_.each(langArray, (lang, index) => {
		languages = `${languages}${languageLookup(lang.language)}: ${lang.percent}%${index < langArray.length - 1 ? ", " : ""}`;
	});

	return languages;
};

export const formatLanguagesFromAPI = (languagesObject) => {
	const languagesArray = [];
	const haveData = languagesObject && languagesObject.top_language;

	if (haveData) {
		_.each(haveData, (langObj) => {
			languagesArray.push(formatLanguageData(langObj));
		});
	}

	return languagesArray;
};

export const isFilterActive = ({ activeFilterMap, id, exclusive }) => {
	const filter = activeFilterMap.get(id);

	if (!filter) {
		return false;
	} else {
		return !!filter.exclusive === !!exclusive;
	}
};

export const isWebSearchSaved = ({ queryId, queryString, domainFilters, languageFilters, savedSearches }) => {
	const activeSearch =
		queryId &&
		_.find(savedSearches, (search) => {
			return search.id === queryId;
		});

	if (!activeSearch) {
		return false;
	} else {
		const queryIsDifferent = allQuotesDouble(activeSearch.query) !== allQuotesDouble(queryString);
		if (queryIsDifferent) {
			return false;
		} else {
			const filtersList = [...domainFilters.values(), ...languageFilters.values()];
			const filtersAlsoTheSame = !haveWebPagesFiltersChanged({ filtersList, queryId, savedSearches });

			return filtersAlsoTheSame;
		}
	}
};

// takes a stringified domain object and retuns the keys as a comma separated string
export const keysStringFromStringifiedObject = (string) => {
	if (string) {
		const obj = JSON.parse(string);
		const keysArray = Object.keys(obj);
		return keysArray.join(",");
	}
};

// converts an array of filter to a Map
//	Creates a new Map, so it will invalidate
export const mapFilterArray = (filtersArray) => {
	const filterMap = new Map();

	if (filtersArray) {
		_.each(filtersArray, (filter) => {
			filterMap.set(filter.id, filter);
		});
	}

	return filterMap;
};

// returns an object with:
//		hydratedDomainArray - array of domain objects
//		domainDataLookup - an object with domain objects with the id hash as the key
export const mapDomains = ({ domainMaps, domainString }) => {
	const hydratedDomainArray = [];
	const domainDataObject = {};

	if (domainString) {
		const domainObj = JSON.parse(domainString);

		_.each(domainObj, (count, hash) => {
			const domainData = _.find(domainMaps, (dataObj) => {
				return hash === dataObj.domain_hash;
			});

			if (domainData) {
				const cleanDomain = domainData.domain.replace(/^(http|https):\/\//, "");
				const hydrated = {
					count,
					id: hash,
					name: validDomainName({ title: domainData.title, domain: cleanDomain }),
					title: domainData.title,
					domain: cleanDomain,
				};
				hydratedDomainArray.push(hydrated);

				domainDataObject[hash] = hydrated;
			}
		});
	}

	return { hydratedDomainArray, domainDataObject };
};

// returns the page title or false
export const pageTitleLookup = (domainObj) => {
	return false;
};

export const rehydrateDoubleQuotes = (arrayOfObjects) => {
	if (!arrayOfObjects || arrayOfObjects.length === 0) {
		return [];
	} else {
		const clonedArray = JSON.parse(JSON.stringify(arrayOfObjects));

		_.each(clonedArray, (search) => {
			search.query = forceQueryDoubleQuotes(search.query);
		});

		return clonedArray;
	}
};

// returns cloned filterMap Map object
//		filterMap		- Map of filter objects: id, { id, name, exclusive }
//		filter						- object: { id, name, exclusive }
export const removeFilter = ({ filterMap, filter }) => {
	const updatedFilterMap = new Map(filterMap);
	updatedFilterMap.delete(filter.id);

	return updatedFilterMap;
};

export const shouldWeShowSaveWebFilterPrompt = ({ domainFilters, languageFilters, queryId, savedSearches }) => {
	const activeSearch =
		queryId &&
		_.find(savedSearches, (search) => {
			return search.id === queryId;
		});

	if (!activeSearch) {
		return false;
	} else {
		// convert filter maps into a single array
		const domains = [...domainFilters.values()];
		const languages = [...languageFilters.values()];
		const currentFilters = [...domains, ...languages];

		// hydrate saved filters & convert into a flat array
		const savedFilterObj = activeSearch.filterStr ? JSON.parse(allQuotesDoubleTemporary(activeSearch.filterStr)) : {};
		const searchDomainFilters = savedFilterObj.domainFilters || [];
		const searchLanguageFilters = savedFilterObj.languageFilters || [];
		const savedFilters = [...searchDomainFilters, ...searchLanguageFilters];

		// convert filter sets into easily comparable data structures
		const savedFilterIds = _.pluck(savedFilters, "id");
		const currentFilterIds = _.pluck(currentFilters, "id");
		const filtersAreDifferent = !_.isEqual(savedFilterIds, currentFilterIds);
		return filtersAreDifferent;
	}
};

const swapSingleQuotesForDouble = (trimmedString) => {
	const startsWithSingle = trimmedString[0] === "'";
	const endsWithSingle = trimmedString[trimmedString.length - 1] === "'";

	if (startsWithSingle && endsWithSingle) {
		const stub = trimmedString.substring(1, trimmedString.length - 1);
		return `"${stub}"`;
	} else {
		return trimmedString;
	}
};

export const stringifyWebSearchFilters = ({ domainFilters, languageFilters }) => {
	return allQuotesSingle(JSON.stringify({ domainFilters, languageFilters }));
};

// returns cloned filterMap Map object
//		filterMap		- Map of filter objects: id, { id, name, exclusive }
//		filter						- object: { id, name, exclusive }
//		exclusive					- boolean
export const toggleFilter = ({ filterMap, filter, exclusive }) => {
	const updatedFilterMap = new Map(filterMap);
	const formattedFilter = JSON.parse(JSON.stringify(filter));
	formattedFilter.exclusive = !!exclusive;
	const oldFilter = filterMap.get(formattedFilter.id);

	const needToRemoveFilter = oldFilter && !!oldFilter.exclusive === !!exclusive;
	if (needToRemoveFilter) {
		updatedFilterMap.delete(formattedFilter.id);
	} else {
		updatedFilterMap.set(formattedFilter.id, formattedFilter);
	}

	return updatedFilterMap;
};

// ------- local functions ---------

const haveWebPagesFiltersChanged = ({ filtersList, queryId, savedSearches }) => {
	const activeSearch =
		queryId &&
		_.find(savedSearches, (search) => {
			return search.id === queryId;
		});

	if (!activeSearch) {
		return false;
	} else {
		// hydrate saved filters & convert into a flat array
		const savedFilterObj = activeSearch.filterStr ? JSON.parse(allQuotesDoubleTemporary(activeSearch.filterStr)) : {};
		const domainFilters = savedFilterObj.domainFilters || [];
		const languageFilters = savedFilterObj.languageFilters || [];
		const savedFilters = [...domainFilters, ...languageFilters];

		// convert filter sets into easily comparable data structures
		const savedFilterIdsPlusExclusive = _.map(savedFilters, (filter) => {
			const { id, exclusive } = filter;
			return `${id}_${exclusive ? "ex" : "in"}`;
		});
		const currentFilterIdsPlusExclusive = _.map(filtersList, (filter) => {
			const { id, exclusive } = filter;
			return `${id}_${exclusive ? "ex" : "in"}`;
		});
		const filtersAreDifferent = !_.isEqual(savedFilterIdsPlusExclusive.sort(), currentFilterIdsPlusExclusive.sort());

		return filtersAreDifferent;
	}
};

// returns the first usable iso code string from the array of iso code strings.
// if none are usable, it returns the first unusable string
const firstUsableLanguageCode = (isoCodeArray) => {
	if (isoCodeArray && _.isArray(isoCodeArray)) {
		const usable = _.find(isoCodeArray, (code) => {
			return !!languageLookup(code);
		});
		return usable || isoCodeArray[0];
	}
};

const formatLanguageData = (langObj) => {
	const id = firstUsableLanguageCode(langObj.codes);
	return {
		id,
		name: languageLookup(id),
		filter: langObj.codes,
		count: langObj.count,
	};
};

const languageLookup = (abreviation) => {
	return iso6931.getName(abreviation) || abreviation;
};

export const validDomainName = ({ title, domain }) => {
	const validTitle = title && title !== "ERROR: The requested URL could not be retrieved";
	return validTitle ? title : domain;
};
